import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";
import moment from "moment-timezone";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import Checkbox from "components/forms/Checkbox";
import Select from "components/forms/SelectInput";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";
import { useToast } from "components/toast";

import { updateServicePlan, getServicePlans, getInstantEstimateProducts } from "api/ServicePlans";

import {
  NOTIFICATION_EVENTS,
  NOTIFICATION_RECIPIENTS,
  NotificationProfile,
  PROFILE_LABELS,
  PROFILE_DESCRIPTIONS,
  profileMatrix,
  detectProfile,
} from "./notificationProfiles";

/**
 * MASTER_PLAN.md §3.9 #22 — Edit Plan modal (mockup 13 "Edit plan"
 * button). Covers every §3.3 config surface in one form:
 *   - Details       — name / term dates / committed count / notes
 *   - Pricing       — discount / payment terms / provider terms /
 *                     terms disclosure
 *   - Cadence       — serviceTypesConfig editor (add/remove rows) +
 *                     per-row Recurring Work schedule (cadenceRule)
 *   - Notifications — profile picker + raw event × recipient matrix
 *   - Corrigo       — category mapping + repair defaults
 *
 * Session 45: sections moved from a single stacked scroll to a
 * left-side tab strip.
 *
 * Session 53: the notification-profile helpers (events, recipients,
 * profile presets, detect/stamp) now import from the shared
 * ./notificationProfiles module instead of the local copies that used
 * to live here — the create modal and this edit modal share one source
 * of truth (the logged dedup follow-up). Behavior is identical; the
 * shared PROFILE_DESCRIPTIONS.mixed copy additionally names the weekly
 * digest, which this modal already stamps.
 *
 * Session 53: the Cadence tab now authors the structured Recurring Work
 * schedule (`cadenceRule`) per row, not just the coarse annual/biennial
 * label. Without this, every plan's serviceTypesConfig row had no
 * cadenceRule and the Recurring Work scheduler skipped it — there was
 * no way to turn recurring generation on from the UI at all. See
 * design/CADENCE_SCHEDULER.md §3.2 for the shape. "Recurring Work" is
 * the user-facing name; the persisted key stays `cadenceRule` (code
 * debt, matching the module's cadence* identifiers).
 *
 * Formik state stays lifted at the modal level (one <Formik> wrapping
 * every tab), so switching tabs unmounts inputs but never drops values.
 *
 * Corrigo tab visibility: rendered when this plan already has Corrigo
 * config, OR when any sibling plan on the same client does.
 *
 * Everything is optional — a blank submit produces the same "use
 * defaults" state as create-with-nulls.
 */

const CADENCE_OPTIONS = [
  { value: "annual", label: "Annual" },
  { value: "biennial", label: "Biennial" },
  { value: "ad_hoc", label: "Ad-hoc" },
  { value: "none", label: "Off" },
];

// design/CADENCE_SCHEDULER.md §3.2 — the three Recurring Work rule
// modes. User-facing labels; the persisted `mode` values stay the
// code identifiers the scheduler's rule service reads.
const RECURRING_MODE_OPTIONS = [
  { value: "seasonal", label: "By season" },
  { value: "interval", label: "Every N years" },
  { value: "specific_dates", label: "On specific dates" },
];

const SEASON_OPTIONS = [
  { value: "spring", label: "Spring (Mar 20)" },
  { value: "summer", label: "Summer (Jun 21)" },
  { value: "fall", label: "Fall (Sep 22)" },
  { value: "winter", label: "Winter (Dec 21)" },
];

// Common US operational zones. The scheduler falls back to
// America/Chicago when a rule carries no timezone; we still default the
// picker to it so the stored value is explicit.
const TIMEZONE_OPTIONS = [
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Phoenix", label: "Arizona (Phoenix)" },
];

const DEFAULT_TZ = "America/Chicago";

// §6.1 — the cadence tab's "what work" reference is a real Product from
// the shared Instant-Estimate source, NOT an invented enum. A row's
// `type` holds a product id; legacy string values surface as a labeled
// fallback option so old plans don't silently blank out.
const LEGACY_TYPE_LABELS: Record<string, string> = {
  mulched: "Mulch (legacy)",
  dig_out: "Dig-out (legacy)",
  inspection: "Inspection (legacy)",
};

const PROVIDER_PAYMENT_MODES = [
  { value: "on_client_payment", label: "On client payment" },
  { value: "hard_net", label: "Hard net" },
];

// design/CADENCE_SCHEDULER.md §3.2 — the structured Recurring Work rule
// persisted on each serviceTypesConfig row. Every field optional in the
// form; assembled/validated at save.
type CadenceRuleShape = {
  mode: "seasonal" | "interval" | "specific_dates";
  season?: "spring" | "summer" | "fall" | "winter";
  intervalYears?: number;
  dates?: Array<{ month: number; day: number }>;
  windowStartOffset?: number;
  windowEndOffset?: number;
  timezone?: string;
};

type ServiceTypeRow = {
  type: string;
  // Session 56 (PLAN_WO_INHERITANCE.md option A) — the SERVICE TYPE this
  // product is sold as, the plan-level default a new WO inherits
  // alongside the product. null = "decide at the work order (price
  // both)".
  serviceId: string | null;
  cadence: "annual" | "biennial" | "ad_hoc" | "none";
  defaultOnNewWo: boolean;
  // Recurring Work: present + recurringOn true => scheduler generates.
  recurringOn: boolean;
  cadenceRule: CadenceRuleShape;
  // Editing convenience for specific_dates: a "M/D, M/D" text field the
  // user types, parsed to `dates` at save. Kept on the row so the input
  // is controlled without a nested repeater.
  datesText: string;
};

type CorrigoMapRow = { internal: string; corrigo: string };

type TabKey = "details" | "pricing" | "cadence" | "notifications" | "corrigo";

const TAB_LABELS: Record<TabKey, string> = {
  details: "Details",
  pricing: "Pricing",
  cadence: "Cadence",
  notifications: "Notifications",
  corrigo: "Corrigo",
};

/** Sensible default window per mode, in days around the anchor. */
function defaultRuleForMode(mode: CadenceRuleShape["mode"]): CadenceRuleShape {
  if (mode === "seasonal") {
    return { mode, season: "spring", windowStartOffset: -30, windowEndOffset: 0, timezone: DEFAULT_TZ };
  }
  if (mode === "interval") {
    return { mode, intervalYears: 2, windowStartOffset: -14, windowEndOffset: 14, timezone: DEFAULT_TZ };
  }
  return { mode, dates: [], windowStartOffset: -7, windowEndOffset: 7, timezone: DEFAULT_TZ };
}

/** Serialize a `dates` array back to the "M/D, M/D" text the field shows. */
function datesToText(dates?: Array<{ month: number; day: number }>): string {
  if (!Array.isArray(dates) || !dates.length) return "";
  return dates.map((d) => `${d.month}/${d.day}`).join(", ");
}

/** Parse "7/4, 9/1" into [{month:7,day:4},{month:9,day:1}]; drops junk. */
function parseDatesText(text: string): Array<{ month: number; day: number }> {
  if (!text) return [];
  const out: Array<{ month: number; day: number }> = [];
  for (const chunk of text.split(",")) {
    const [m, d] = chunk.trim().split("/").map((n) => parseInt(n, 10));
    if (Number.isFinite(m) && Number.isFinite(d) && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      out.push({ month: m, day: d });
    }
  }
  return out;
}

/** A plan counts as Corrigo-configured if any Corrigo pricingConfig key
 * carries a value. Drives tab visibility. */
function hasCorrigoConfig(p: any): boolean {
  const pc = p?.pricingConfig || {};
  return !!(
    pc.corrigoCategoryMap ||
    pc.corrigoRepairCategoryDefault ||
    pc.corrigoRepairCodeDefault
  );
}

export default function EditPlanModal({
  plan,
  onClose,
  onSaved,
}: {
  plan: any;
  onClose: () => void;
  onSaved: (plan: any) => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("details");

  // §6.1 — shared Instant-Estimate product source for the cadence tab's
  // Service picker. null = loading, [] = none available.
  const [products, setProducts] = useState<any[] | null>(null);

  useEffect(() => {
    let alive = true;
    getInstantEstimateProducts().then((rows: any[]) => {
      if (alive) setProducts(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const primaryProductId = products && products.length > 0 ? products[0].id : "";

  // ─── Corrigo tab visibility ──────────────────────────────────
  const ownCorrigo = hasCorrigoConfig(plan);
  const [siblingCorrigo, setSiblingCorrigo] = useState(false);

  useEffect(() => {
    if (ownCorrigo) return;
    const clientId = plan?.client?.id;
    if (!clientId) return;

    let cancelled = false;
    (async () => {
      const res: any = await getServicePlans({
        query: { filters: { clientId }, recordsPer: "100" },
      });
      const items: any[] = Array.isArray(res) ? res : res?.items || [];
      const found = items.some((p: any) => p?.id !== plan?.id && hasCorrigoConfig(p));
      if (!cancelled) setSiblingCorrigo(found);
    })();

    return () => {
      cancelled = true;
    };
  }, [plan, ownCorrigo]);

  const showCorrigoTab = ownCorrigo || siblingCorrigo;

  const tabs = useMemo<TabKey[]>(() => {
    const base: TabKey[] = ["details", "pricing", "cadence", "notifications"];
    return showCorrigoTab ? [...base, "corrigo"] : base;
  }, [showCorrigoTab]);

  useEffect(() => {
    if (activeTab === "corrigo" && !showCorrigoTab) setActiveTab("details");
  }, [activeTab, showCorrigoTab]);

  const initial = useMemo(() => {
    const pc = plan?.pricingConfig || {};
    const ppt = pc?.providerPaymentTerms || {};
    const rawTypes: ServiceTypeRow[] = Array.isArray(plan?.serviceTypesConfig)
      ? plan.serviceTypesConfig.map((s: any) => {
          const existingRule = s.cadenceRule && s.cadenceRule.mode ? s.cadenceRule : null;
          const rule: CadenceRuleShape = existingRule
            ? {
                mode: existingRule.mode,
                season: existingRule.season,
                intervalYears: existingRule.intervalYears,
                dates: Array.isArray(existingRule.dates) ? existingRule.dates : undefined,
                windowStartOffset: existingRule.windowStartOffset,
                windowEndOffset: existingRule.windowEndOffset,
                timezone: existingRule.timezone || DEFAULT_TZ,
              }
            : defaultRuleForMode("seasonal");
          return {
            type: s.type || "",
            serviceId: s.serviceId ?? null,
            cadence: s.cadence || "ad_hoc",
            defaultOnNewWo: !!s.defaultOnNewWo,
            recurringOn: !!existingRule,
            cadenceRule: rule,
            datesText: datesToText(rule.dates),
          };
        })
      : [];
    const rawMap: CorrigoMapRow[] = pc?.corrigoCategoryMap
      ? Object.entries(pc.corrigoCategoryMap).map(([k, v]) => ({
          internal: k,
          corrigo: String(v || ""),
        }))
      : [];
    return {
      // Details
      name: plan?.name || "",
      intakeStyle: plan?.intakeStyle === "work_order_fed" ? "work_order_fed" : "quote_fed",
      clientSelfServiceEnabled: !!plan?.clientSelfServiceEnabled,
      isTermBased: !!plan?.endDate,
      endDate: plan?.endDate ? moment(plan.endDate).unix() : undefined,
      committedSiteCount: plan?.committedSiteCount ?? "",
      notes: plan?.notes || "",
      // Pricing
      discountPct: pc.discountPct ?? "",
      paymentTerms: pc.paymentTerms || "",
      showAdvancedPricing: !!(ppt?.mode || ppt?.days || ppt?.falloutDays || pc.providerTermsDisclosure),
      providerPaymentMode: ppt.mode || "",
      providerPaymentDays: ppt.days ?? "",
      providerPaymentFalloutDays: ppt.falloutDays ?? "",
      providerTermsDisclosure: pc.providerTermsDisclosure || "",
      // Cadence
      serviceTypes: rawTypes,
      // Notifications — profile picker + raw matrix (Custom only)
      notificationProfile: detectProfile(plan?.notificationConfig) as NotificationProfile,
      notifications: Object.fromEntries(
        NOTIFICATION_EVENTS.flatMap((e) =>
          NOTIFICATION_RECIPIENTS.map((r) => {
            const key = `${e.key}.${r.key}`;
            return [key, !!plan?.notificationConfig?.[key]];
          }),
        ),
      ) as Record<string, boolean>,
      // Corrigo
      corrigoMap: rawMap,
      corrigoRepairCategory: pc.corrigoRepairCategoryDefault || "",
      corrigoRepairCode: pc.corrigoRepairCodeDefault || "",
    };
  }, [plan]);

  const _validate = Yup.object().shape({
    name: Yup.string().required("Plan name is required."),
    discountPct: Yup.number().nullable().min(0, "0 or more.").max(100, "100 or less."),
    providerPaymentDays: Yup.number().nullable().min(0),
    providerPaymentFalloutDays: Yup.number().nullable().min(0),
  });

  const submit = async (values: any) => {
    setSubmitting(true);

    const nextPricing: Record<string, any> = { ...(plan?.pricingConfig || {}) };
    nextPricing.discountPct = values.discountPct === "" ? null : Number(values.discountPct);
    nextPricing.paymentTerms = values.paymentTerms || null;
    nextPricing.providerTermsDisclosure = values.providerTermsDisclosure || null;

    const ppt: Record<string, any> = {};
    if (values.providerPaymentMode) ppt.mode = values.providerPaymentMode;
    if (values.providerPaymentDays !== "") ppt.days = Number(values.providerPaymentDays);
    if (values.providerPaymentFalloutDays !== "") ppt.falloutDays = Number(values.providerPaymentFalloutDays);
    nextPricing.providerPaymentTerms = Object.keys(ppt).length ? ppt : null;

    const map: Record<string, string> = {};
    for (const r of values.corrigoMap || []) {
      if (r.internal && r.corrigo) map[r.internal] = r.corrigo;
    }
    nextPricing.corrigoCategoryMap = Object.keys(map).length ? map : null;
    nextPricing.corrigoRepairCategoryDefault = values.corrigoRepairCategory || null;
    nextPricing.corrigoRepairCodeDefault = values.corrigoRepairCode || null;

    // ─── serviceTypesConfig (+ Recurring Work rule) ──────────
    // A row persists its structured cadenceRule ONLY when recurringOn is
    // checked AND the assembled rule is valid for its mode. Otherwise
    // cadenceRule is omitted and the scheduler skips the row — an
    // explicit "off" without deleting the product's coarse cadence line.
    const nextTypes = (values.serviceTypes || [])
      .filter((r: ServiceTypeRow) => !!r.type)
      .map((r: ServiceTypeRow) => {
        const base: any = {
          type: r.type,
          serviceId: r.serviceId || null,
          cadence: r.cadence,
          defaultOnNewWo: !!r.defaultOnNewWo,
        };
        const rule = assembleRule(r);
        if (rule) base.cadenceRule = rule;
        return base;
      });

    const nextNotif: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(values.notifications || {})) {
      if (v) nextNotif[k] = true;
    }

    const update = {
      name: values.name,
      intakeStyle: values.intakeStyle === "work_order_fed" ? "work_order_fed" : "quote_fed",
      // KNOWN_ISSUES item 18 - the per-plan self-service grant. Sent as a
      // hard boolean: this opens a WRITE path on a portal that is
      // otherwise read-only, so an undefined must never be read as leaving
      // it on.
      clientSelfServiceEnabled: !!values.clientSelfServiceEnabled,
      endDate: values.isTermBased && values.endDate
        ? moment.unix(values.endDate).format("YYYY-MM-DD")
        : null,
      committedSiteCount:
        values.isTermBased && values.committedSiteCount !== ""
          ? Number(values.committedSiteCount)
          : null,
      notes: values.notes || null,
      pricingConfig: nextPricing,
      serviceTypesConfig: nextTypes.length ? nextTypes : null,
      notificationConfig: Object.keys(nextNotif).length ? nextNotif : null,
    };

    const res = await updateServicePlan({
      data: { id: plan.id },
      update,
    });
    setSubmitting(false);

    if (res?.id) {
      toast.add("Plan updated.", "var(--theme-ui-colors-success)");
      onSaved(res);
    } else {
      toast.add(res?.message || "Error updating plan.", "var(--theme-ui-colors-danger)");
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Edit Plan</ModalHeader>
      <Formik initialValues={initial} enableReinitialize validationSchema={_validate} onSubmit={submit}>
        {({ values, errors, submitCount, setFieldValue }) => (
          <Form noValidate autoComplete="off">
            <ModalBody>
              <Flex sx={{ gap: "20px", alignItems: "stretch", flexDirection: ["column", "row"] }}>

                {/* ───── Tab strip ───── */}
                <Box
                  sx={{
                    flex: "0 0 auto",
                    width: ["100%", "168px"],
                    borderRight: [null, "1px solid"],
                    borderBottom: ["1px solid", "none"],
                    borderColor: "inputOutline",
                    paddingRight: [0, "12px"],
                    paddingBottom: ["12px", 0],
                  }}
                >
                  <Flex sx={{ flexDirection: ["row", "column"], gap: "4px", flexWrap: "wrap" }}>
                    {tabs.map((t) => {
                      const active = activeTab === t;
                      const invalidHere =
                        t === "details" && !!errors.name && submitCount > 0;
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setActiveTab(t)}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            background: active ? mnt.bgBeige : "transparent",
                            color: invalidHere
                              ? mnt.red
                              : active
                              ? mnt.ink
                              : mnt.textMuted,
                            border: "none",
                            borderLeft: active
                              ? "2px solid " + mnt.ink
                              : "2px solid transparent",
                            borderRadius: "4px",
                            padding: "9px 12px",
                            fontSize: "13px",
                            fontFamily: "Poppins",
                            fontWeight: active ? 600 : 400,
                            cursor: "pointer",
                          }}
                        >
                          {TAB_LABELS[t]}
                        </button>
                      );
                    })}
                  </Flex>
                </Box>

                {/* ───── Active section ───── */}
                <Box
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    maxHeight: "62vh",
                    overflowY: "auto",
                    paddingRight: "4px",
                  }}
                >

                  {activeTab === "details" && (
                    <Section title="Details">
                      <TextInput
                        name="name"
                        label="Plan Name"
                        required
                        value={values.name}
                        onChange={(v: any) => setFieldValue("name", v)}
                        $errors={errors.name && submitCount > 0 ? errors.name : null}
                      />
                      <Box sx={{ marginTop: "16px" }}>
                        <Subtext
                          fontSize={"12px"}
                          fontFamily={"Poppins"}
                          margin={0}
                          marginBottom={"6px"}
                          color={themevals.colors.dkgrey}
                        >
                          How work enters this plan
                        </Subtext>
                        <Flex sx={{ gap: "1.5rem" }}>
                          <Checkbox
                            label="Quote-fed"
                            checked={values.intakeStyle === "quote_fed"}
                            onChange={() => setFieldValue("intakeStyle", "quote_fed")}
                            labelSize="14px"
                          />
                          <Checkbox
                            label="Work-order-fed"
                            checked={values.intakeStyle === "work_order_fed"}
                            onChange={() => setFieldValue("intakeStyle", "work_order_fed")}
                            labelSize="14px"
                          />
                        </Flex>
                        <Subtext
                          fontSize={"12px"}
                          fontFamily={"Poppins"}
                          margin={0}
                          marginTop={"5px"}
                          color={themevals.colors.dkgrey}
                        >
                          Quote-fed issues a quote directly at a location; the client accepts it. Work-order-fed shows the work order queue and convert flow (EMCOR / Corrigo). Switching hides or shows those tools.
                        </Subtext>
                      </Box>
                      {/* KNOWN_ISSUES item 18 - the per-plan self-service
                          grant. A SEPARATE AXIS from intake style above:
                          that says how work ENTERS the plan, this says
                          whether the client may START it. Either
                          combination is valid. Off on every existing
                          plan, and the endpoint re-checks it server-side,
                          so this switch IS the grant rather than a UI
                          convenience. */}
                      <Box sx={{ marginTop: "18px" }}>
                        <Subtext
                          fontSize={"12px"}
                          fontFamily={"Poppins"}
                          margin={0}
                          marginBottom={"6px"}
                          color={themevals.colors.dkgrey}
                        >
                          Client self-service
                        </Subtext>
                        <Checkbox
                          label="Let this client request work from the portal"
                          checked={values.clientSelfServiceEnabled === true}
                          onChange={(val: any) => setFieldValue("clientSelfServiceEnabled", !!val)}
                          labelSize="14px"
                          sublabel="Adds a Request work button to their plan page and their locations list. Requests arrive in the work order queue marked as client requests. Any urgency they state is advisory and never sets a date."
                        />
                      </Box>
                      <Box sx={{ marginTop: "16px" }}>
                        <Flex sx={{ gap: "1.5rem" }}>
                          <Checkbox
                            label="Ongoing"
                            checked={!values.isTermBased}
                            onChange={() => {
                              setFieldValue("isTermBased", false);
                              setFieldValue("endDate", undefined);
                              setFieldValue("committedSiteCount", "");
                            }}
                            labelSize="14px"
                          />
                          <Checkbox
                            label="Term-based"
                            checked={values.isTermBased}
                            onChange={() => setFieldValue("isTermBased", true)}
                            labelSize="14px"
                          />
                        </Flex>
                      </Box>
                      {values.isTermBased && (
                        <Grid columns={[1, 2]} gap={3} sx={{ marginTop: "12px" }}>
                          <SelectDate
                            name="endDate"
                            label="Target End Date"
                            value={values.endDate}
                            onChange={(v: any) => setFieldValue("endDate", v)}
                          />
                          <TextInput
                            type="number"
                            name="committedSiteCount"
                            label="Committed Locations"
                            min={0}
                            value={values.committedSiteCount}
                            onChange={(v: any) => setFieldValue("committedSiteCount", v)}
                          />
                        </Grid>
                      )}
                      <Box sx={{ marginTop: "16px" }}>
                        <TextInput
                          type="textarea"
                          name="notes"
                          label="Notes"
                          rows={2}
                          value={values.notes}
                          onChange={(v: any) => setFieldValue("notes", v)}
                        />
                      </Box>
                    </Section>
                  )}

                  {activeTab === "pricing" && (
                    <Section title="Pricing">
                      <Grid columns={[1, 2]} gap={3}>
                        <TextInput
                          type="number"
                          name="discountPct"
                          label="Discount (%)"
                          min={0}
                          max={100}
                          value={values.discountPct}
                          onChange={(v: any) => setFieldValue("discountPct", v)}
                          $errors={errors.discountPct && submitCount > 0 ? errors.discountPct : null}
                          placeholder="Blank = standard rates"
                        />
                        <TextInput
                          name="paymentTerms"
                          label="Client Payment Terms"
                          value={values.paymentTerms}
                          onChange={(v: any) => setFieldValue("paymentTerms", v)}
                          placeholder="e.g. net60, on-receipt"
                        />
                      </Grid>
                      <SectionSubHeader label="Provider Payment Terms" />
                      <Grid columns={[1, 3]} gap={3}>
                        <Select
                          name="providerPaymentMode"
                          label="Mode"
                          value={values.providerPaymentMode}
                          onChange={(v: any) => setFieldValue("providerPaymentMode", v)}
                          options={[{ value: "", label: "(default)" }, ...PROVIDER_PAYMENT_MODES]}
                        />
                        <TextInput
                          type="number"
                          name="providerPaymentDays"
                          label="Days"
                          min={0}
                          value={values.providerPaymentDays}
                          onChange={(v: any) => setFieldValue("providerPaymentDays", v)}
                          placeholder="60"
                        />
                        <TextInput
                          type="number"
                          name="providerPaymentFalloutDays"
                          label="Fallout Days"
                          min={0}
                          value={values.providerPaymentFalloutDays}
                          onChange={(v: any) => setFieldValue("providerPaymentFalloutDays", v)}
                          placeholder="90"
                        />
                      </Grid>
                      <Box sx={{ marginTop: "16px" }}>
                        <TextInput
                          type="textarea"
                          name="providerTermsDisclosure"
                          label="Provider Terms Disclosure (PO PDF + acknowledgment modal)"
                          rows={3}
                          value={values.providerTermsDisclosure}
                          onChange={(v: any) => setFieldValue("providerTermsDisclosure", v)}
                          placeholder="Rendered on POs when provider payment terms are set. Provider must acknowledge before Accept."
                        />
                      </Box>
                    </Section>
                  )}

                  {activeTab === "cadence" && (
                    <Section title="Cadence">
                      <Subtext
                        fontSize={"12px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginBottom={"12px"}
                        color={themevals.colors.dkgrey}
                      >
                        One row per product this plan re-services on a schedule. Pick the product (e.g. Playground Mulch) the same way an Instant Estimate does; the delivery/install service and any add-ons resolve automatically from the site location and provider at quote time. Turn on &quot;Recurring Work&quot; to have the system auto-create work orders on the schedule you set. Rows showing a &quot;(legacy)&quot; label predate this and should be re-picked as a real product. These are DEFAULTS for new work orders — the product and service type here pre-fill a new work order on this plan, which can still override them. Changing a default does not touch work orders that already exist.
                      </Subtext>
                      {(values.serviceTypes || []).map((row: ServiceTypeRow, idx: number) => (
                        <Box
                          key={idx}
                          sx={{
                            border: "1px solid",
                            borderColor: "inputOutline",
                            borderRadius: "8px",
                            padding: "12px 14px",
                            marginBottom: "12px",
                          }}
                        >
                          <Grid
                            columns={["1fr 1fr auto"]}
                            gap={3}
                            sx={{ alignItems: "end" }}
                          >
                            <Select
                              name={`serviceTypes.${idx}.type`}
                              label={"Product"}
                              value={row.type}
                              onChange={(v: any) => {
                                const next = [...values.serviceTypes];
                                // Services are per product, so a product
                                // change invalidates the service default.
                                next[idx] = { ...next[idx], type: v, serviceId: null };
                                setFieldValue("serviceTypes", next);
                              }}
                              options={buildServiceOptions(products, row.type)}
                            />
                            <Select
                              name={`serviceTypes.${idx}.cadence`}
                              label={"Cadence"}
                              value={row.cadence}
                              onChange={(v: any) => {
                                const next = [...values.serviceTypes];
                                next[idx] = { ...next[idx], cadence: v };
                                setFieldValue("serviceTypes", next);
                              }}
                              options={CADENCE_OPTIONS}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const next = [...values.serviceTypes];
                                next.splice(idx, 1);
                                setFieldValue("serviceTypes", next);
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: themevals.colors.danger || "#791F1F",
                                fontSize: "12px",
                                padding: "6px 0",
                                cursor: "pointer",
                              }}
                            >
                              Remove
                            </button>
                          </Grid>

                          {/* Session 56 (PLAN_WO_INHERITANCE.md option A)
                              — the default SERVICE TYPE a new work order on
                              this plan inherits with the product. Options
                              are the services this product is sold as;
                              blank means decide at the work order. */}
                          <Box sx={{ marginTop: "10px" }}>
                            <Select
                              name={`serviceTypes.${idx}.serviceId`}
                              label={"Default service type (new work orders)"}
                              value={row.serviceId || ""}
                              onChange={(v: any) => {
                                const next = [...values.serviceTypes];
                                next[idx] = { ...next[idx], serviceId: v || null };
                                setFieldValue("serviceTypes", next);
                              }}
                              options={serviceTypeOptionsForProduct(products, row.type, row.serviceId)}
                            />
                          </Box>

                          <Flex sx={{ gap: "1.5rem", marginTop: "10px", alignItems: "center" }}>
                            <Checkbox
                              label="Default on new WO"
                              checked={!!row.defaultOnNewWo}
                              onChange={() => {
                                const next = [...values.serviceTypes];
                                next[idx] = { ...next[idx], defaultOnNewWo: !next[idx].defaultOnNewWo };
                                setFieldValue("serviceTypes", next);
                              }}
                              labelSize="12px"
                            />
                            <Checkbox
                              label="Recurring Work (auto-create WOs)"
                              checked={!!row.recurringOn}
                              onChange={() => {
                                const next = [...values.serviceTypes];
                                const turningOn = !next[idx].recurringOn;
                                const rule =
                                  next[idx].cadenceRule && next[idx].cadenceRule.mode
                                    ? next[idx].cadenceRule
                                    : defaultRuleForMode("seasonal");
                                next[idx] = {
                                  ...next[idx],
                                  recurringOn: turningOn,
                                  cadenceRule: rule,
                                  datesText: datesToText(rule.dates),
                                };
                                setFieldValue("serviceTypes", next);
                              }}
                              labelSize="12px"
                            />
                          </Flex>

                          {row.recurringOn && (
                            <RecurringWorkEditor
                              row={row}
                              idx={idx}
                              serviceTypes={values.serviceTypes}
                              setFieldValue={setFieldValue}
                            />
                          )}
                        </Box>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setFieldValue("serviceTypes", [
                            ...(values.serviceTypes || []),
                            {
                              type: primaryProductId,
                              serviceId: null,
                              cadence: "annual",
                              defaultOnNewWo: false,
                              recurringOn: false,
                              cadenceRule: defaultRuleForMode("seasonal"),
                              datesText: "",
                            },
                          ]);
                        }}
                        style={{
                          background: "transparent",
                          border: "1px dashed #D3D1C7",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "12px",
                          fontFamily: "Poppins",
                          color: themevals.colors.info || "#185FA5",
                          cursor: "pointer",
                          marginTop: "6px",
                        }}
                      >
                        + Add product
                      </button>
                    </Section>
                  )}

                  {activeTab === "notifications" && (
                    <Section title="Notifications">
                      <Subtext
                        fontSize={"12px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginBottom={"12px"}
                        color={themevals.colors.dkgrey}
                      >
                        Pick a profile to stamp defaults, or choose Custom to fine-tune every event × recipient.
                      </Subtext>
                      <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                        {(Object.keys(PROFILE_LABELS) as NotificationProfile[]).map((p) => {
                          const active = values.notificationProfile === p;
                          return (
                            <button
                              key={p}
                              type="button"
                              onClick={() => {
                                setFieldValue("notificationProfile", p);
                                if (p !== "custom") {
                                  setFieldValue("notifications", profileMatrix(p));
                                }
                              }}
                              style={{
                                background: active ? mnt.ink : mnt.cardBg,
                                color: active ? mnt.cardBg : mnt.ink,
                                border: active
                                  ? "1px solid " + mnt.ink
                                  : "1px solid " + mnt.divider,
                                borderRadius: "4px",
                                padding: "8px 14px",
                                fontSize: "12px",
                                fontFamily: "Poppins",
                                cursor: "pointer",
                              }}
                            >
                              {PROFILE_LABELS[p]}
                            </button>
                          );
                        })}
                      </Flex>
                      <Subtext
                        fontSize={"11px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginBottom={"12px"}
                        color={themevals.colors.dkgrey}
                      >
                        {PROFILE_DESCRIPTIONS[values.notificationProfile as NotificationProfile]}
                      </Subtext>
                      {values.notificationProfile === "custom" && (
                        <Box
                          sx={{
                            background: mnt.bgSubtle,
                            border: "1px solid",
                            borderColor: "inputOutline",
                            borderRadius: "6px",
                            padding: "10px",
                            overflowX: "auto",
                          }}
                        >
                          <Box as="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: "left", padding: "6px", fontSize: "11px", color: mnt.textMuted }}>
                                  Event
                                </th>
                                {NOTIFICATION_RECIPIENTS.map((r) => (
                                  <th
                                    key={r.key}
                                    style={{
                                      textAlign: "center",
                                      padding: "6px",
                                      fontSize: "11px",
                                      color: mnt.textMuted,
                                      minWidth: "110px",
                                    }}
                                  >
                                    {r.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {NOTIFICATION_EVENTS.map((e) => (
                                <tr key={e.key} style={{ borderTop: "0.5px solid " + mnt.divider }}>
                                  <td style={{ padding: "8px 6px", fontSize: "12px" }}>{e.label}</td>
                                  {NOTIFICATION_RECIPIENTS.map((r) => {
                                    const key = `${e.key}.${r.key}`;
                                    const checked = !!values.notifications[key];
                                    return (
                                      <td key={r.key} style={{ padding: "8px 6px", textAlign: "center" }}>
                                        <Checkbox
                                          checked={checked}
                                          onChange={() =>
                                            setFieldValue("notifications", {
                                              ...values.notifications,
                                              [key]: !checked,
                                            })
                                          }
                                          labelSize="12px"
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </Box>
                        </Box>
                      )}
                    </Section>
                  )}

                  {activeTab === "corrigo" && showCorrigoTab && (
                    <Section title="Corrigo settings">
                      <Subtext
                        fontSize={"12px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginBottom={"12px"}
                        color={themevals.colors.dkgrey}
                      >
                        Category map is per-account (EMCOR&apos;s dropdown values differ from other FM accounts).
                      </Subtext>
                      <SectionSubHeader label="Category mapping (internal → Corrigo dropdown)" />
                      {(values.corrigoMap || []).map((row: CorrigoMapRow, idx: number) => (
                        <Grid
                          key={idx}
                          columns={["1fr 1fr auto"]}
                          gap={3}
                          sx={{ marginBottom: "8px", alignItems: "end" }}
                        >
                          <TextInput
                            name={`corrigoMap.${idx}.internal`}
                            label={idx === 0 ? "Internal category" : ""}
                            value={row.internal}
                            onChange={(v: any) => {
                              const next = [...values.corrigoMap];
                              next[idx] = { ...next[idx], internal: v };
                              setFieldValue("corrigoMap", next);
                            }}
                            placeholder="mulch_material"
                          />
                          <TextInput
                            name={`corrigoMap.${idx}.corrigo`}
                            label={idx === 0 ? "Corrigo dropdown value" : ""}
                            value={row.corrigo}
                            onChange={(v: any) => {
                              const next = [...values.corrigoMap];
                              next[idx] = { ...next[idx], corrigo: v };
                              setFieldValue("corrigoMap", next);
                            }}
                            placeholder="Service Provider Material (EFS1)"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = [...values.corrigoMap];
                              next.splice(idx, 1);
                              setFieldValue("corrigoMap", next);
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: themevals.colors.danger || "#791F1F",
                              fontSize: "12px",
                              padding: "6px 0",
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        </Grid>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setFieldValue("corrigoMap", [
                            ...(values.corrigoMap || []),
                            { internal: "", corrigo: "" },
                          ])
                        }
                        style={{
                          background: "transparent",
                          border: "1px dashed #D3D1C7",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "12px",
                          fontFamily: "Poppins",
                          color: themevals.colors.info || "#185FA5",
                          cursor: "pointer",
                        }}
                      >
                        + Add mapping
                      </button>
                      <SectionSubHeader label="Repair defaults (WO Completion Details)" />
                      <Grid columns={[1, 2]} gap={3}>
                        <TextInput
                          name="corrigoRepairCategory"
                          label="Repair Category"
                          value={values.corrigoRepairCategory}
                          onChange={(v: any) => setFieldValue("corrigoRepairCategory", v)}
                          placeholder="e.g. Landscape"
                        />
                        <TextInput
                          name="corrigoRepairCode"
                          label="Repair Code"
                          value={values.corrigoRepairCode}
                          onChange={(v: any) => setFieldValue("corrigoRepairCode", v)}
                          placeholder="e.g. LSC-01"
                        />
                      </Grid>
                    </Section>
                  )}

                </Box>
              </Flex>
            </ModalBody>

            <ModalFooter>
              <BasicButton type="button" outline scheme="secondary" onClick={onClose}>
                Cancel
              </BasicButton>
              <BasicButton
                type="submit"
                $submitting={submitting}
                $iconHeight="14px"
                $iconWidth="14px"
                $loadingColor="#fff"
                keepText={true}
              >
                {!submitting && <CheckMarkIcon fill={"#fff"} />}
                Save Plan
              </BasicButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

/**
 * design/CADENCE_SCHEDULER.md §3.2/§6 — per-row Recurring Work rule
 * editor. Renders the mode picker and the fields that mode needs, plus
 * the shared window offsets and timezone. Writes back into the row's
 * `cadenceRule` (and `datesText` for the specific-dates input) on the
 * lifted serviceTypes array.
 */
function RecurringWorkEditor({
  row,
  idx,
  serviceTypes,
  setFieldValue,
}: {
  row: ServiceTypeRow;
  idx: number;
  serviceTypes: ServiceTypeRow[];
  setFieldValue: (field: string, value: any) => void;
}) {
  const rule = row.cadenceRule || defaultRuleForMode("seasonal");

  const patchRule = (patch: Partial<CadenceRuleShape>, extra?: Partial<ServiceTypeRow>) => {
    const next = [...serviceTypes];
    next[idx] = {
      ...next[idx],
      cadenceRule: { ...next[idx].cadenceRule, ...patch },
      ...(extra || {}),
    };
    setFieldValue("serviceTypes", next);
  };

  return (
    <Box
      sx={{
        marginTop: "12px",
        paddingTop: "12px",
        borderTop: "1px dashed " + mnt.divider,
      }}
    >
      <Subtext
        fontSize={"11px"}
        fontFamily={"Poppins"}
        fontWeight={600}
        margin={0}
        marginBottom={"8px"}
        color={themevals.colors.dkgrey}
      >
        RECURRING WORK SCHEDULE
      </Subtext>

      <Grid columns={[1, 2]} gap={3}>
        <Select
          name={`serviceTypes.${idx}.cadenceRule.mode`}
          label="Schedule type"
          value={rule.mode}
          onChange={(v: any) => {
            // Switching mode resets to that mode's sensible defaults but
            // keeps the timezone the admin already chose.
            const tz = rule.timezone || DEFAULT_TZ;
            const fresh = defaultRuleForMode(v);
            fresh.timezone = tz;
            patchRule(fresh, { datesText: datesToText(fresh.dates) });
          }}
          options={RECURRING_MODE_OPTIONS}
        />
        <Select
          name={`serviceTypes.${idx}.cadenceRule.timezone`}
          label="Timezone"
          value={rule.timezone || DEFAULT_TZ}
          onChange={(v: any) => patchRule({ timezone: v })}
          options={TIMEZONE_OPTIONS}
        />
      </Grid>

      {rule.mode === "seasonal" && (
        <Box sx={{ marginTop: "10px" }}>
          <Select
            name={`serviceTypes.${idx}.cadenceRule.season`}
            label="Season"
            value={rule.season || "spring"}
            onChange={(v: any) => patchRule({ season: v })}
            options={SEASON_OPTIONS}
          />
        </Box>
      )}

      {rule.mode === "interval" && (
        <Box sx={{ marginTop: "10px" }}>
          <TextInput
            type="number"
            name={`serviceTypes.${idx}.cadenceRule.intervalYears`}
            label="Repeat every (years)"
            min={1}
            value={rule.intervalYears ?? ""}
            onChange={(v: any) =>
              patchRule({ intervalYears: v === "" ? undefined : Number(v) })
            }
            placeholder="2"
          />
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"4px"}
            color={themevals.colors.dkgrey}
          >
            Counts from the last recorded service at each site. New sites count from when they joined the plan.
          </Subtext>
        </Box>
      )}

      {rule.mode === "specific_dates" && (
        <Box sx={{ marginTop: "10px" }}>
          <TextInput
            name={`serviceTypes.${idx}.datesText`}
            label="Dates (month/day, comma-separated)"
            value={row.datesText || ""}
            onChange={(v: any) =>
              patchRule({ dates: parseDatesText(v) }, { datesText: v })
            }
            placeholder="7/4, 9/1"
          />
        </Box>
      )}

      <Grid columns={[1, 2]} gap={3} sx={{ marginTop: "10px" }}>
        <TextInput
          type="number"
          name={`serviceTypes.${idx}.cadenceRule.windowStartOffset`}
          label="Start window (days before)"
          value={rule.windowStartOffset ?? ""}
          onChange={(v: any) =>
            patchRule({ windowStartOffset: v === "" ? undefined : Number(v) })
          }
          placeholder="-30"
        />
        <TextInput
          type="number"
          name={`serviceTypes.${idx}.cadenceRule.windowEndOffset`}
          label="End window (days after)"
          value={rule.windowEndOffset ?? ""}
          onChange={(v: any) =>
            patchRule({ windowEndOffset: v === "" ? undefined : Number(v) })
          }
          placeholder="0"
        />
      </Grid>
      <Subtext
        fontSize={"11px"}
        fontFamily={"Poppins"}
        margin={0}
        marginTop={"6px"}
        color={themevals.colors.dkgrey}
      >
        The window is how many days around the target date the system may generate the work order. Negative starts earlier, positive ends later.
      </Subtext>
    </Box>
  );
}

/**
 * Assemble a row's editing state into the persisted cadenceRule, or
 * null when the row shouldn't schedule (recurring off, or the rule is
 * incomplete for its mode). Null → the save mapping omits cadenceRule
 * and the scheduler skips the row.
 */
function assembleRule(row: ServiceTypeRow): CadenceRuleShape | null {
  if (!row.recurringOn) return null;
  const r = row.cadenceRule;
  if (!r || !r.mode) return null;

  const windowStartOffset =
    typeof r.windowStartOffset === "number" ? r.windowStartOffset : 0;
  const windowEndOffset =
    typeof r.windowEndOffset === "number" ? r.windowEndOffset : 0;
  const timezone = r.timezone || DEFAULT_TZ;

  if (r.mode === "seasonal") {
    if (!r.season) return null;
    return { mode: "seasonal", season: r.season, windowStartOffset, windowEndOffset, timezone };
  }
  if (r.mode === "interval") {
    if (!r.intervalYears || r.intervalYears < 1) return null;
    return {
      mode: "interval",
      intervalYears: r.intervalYears,
      windowStartOffset,
      windowEndOffset,
      timezone,
    };
  }
  if (r.mode === "specific_dates") {
    const dates = Array.isArray(r.dates) ? r.dates : parseDatesText(row.datesText);
    if (!dates.length) return null;
    return { mode: "specific_dates", dates, windowStartOffset, windowEndOffset, timezone };
  }
  return null;
}

/**
 * §6.1 — build the cadence-row Service picker options from the shared
 * Instant-Estimate product set. Always includes the row's current value
 * so nothing blanks out.
 */
function buildServiceOptions(
  products: any[] | null,
  currentValue: string,
): Array<{ value: string; label: string }> {
  const opts: Array<{ value: string; label: string }> = [];
  const seen = new Set<string>();

  for (const p of products || []) {
    opts.push({ value: p.id, label: p.name });
    seen.add(p.id);
  }

  if (currentValue && !seen.has(currentValue)) {
    const legacyLabel =
      LEGACY_TYPE_LABELS[currentValue] || `Unavailable product (${currentValue.slice(0, 8)}…)`;
    opts.unshift({ value: currentValue, label: legacyLabel });
  }

  if (opts.length === 0) {
    opts.push({ value: "", label: "No products available" });
  }

  return opts;
}

/**
 * Session 56 (PLAN_WO_INHERITANCE.md option A) — the service-type
 * options for a cadence row's product, from the same shared product set
 * (each product carries the services it can be sold as). Leads with a
 * "decide at the work order" blank so a plan can default the product but
 * leave the service open. Always includes the row's current value so a
 * stored service never blanks out even if the product list shifts.
 */
function serviceTypeOptionsForProduct(
  products: any[] | null,
  productId: string,
  currentValue: string | null,
): Array<{ value: string; label: string }> {
  const product = (products || []).find((p) => p.id === productId);
  const svcs: Array<{ id: string; name: string }> = Array.isArray(product?.services)
    ? product.services
    : [];
  const opts: Array<{ value: string; label: string }> = [
    { value: "", label: "Decide at the work order (price both)" },
    ...svcs.map((s) => ({ value: s.id, label: s.name })),
  ];
  if (currentValue && !svcs.some((s) => s.id === currentValue)) {
    opts.push({
      value: currentValue,
      label: `Selected service (${currentValue.slice(0, 8)}…)`,
    });
  }
  return opts;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ marginBottom: 0, paddingBottom: 0 }}>
      <H5 fontFamily="Poppins" fontWeight={600} fontSize={"14px"} margin={0} marginBottom={"14px"}>
        {title}
      </H5>
      {children}
    </Box>
  );
}

function SectionSubHeader({ label }: { label: string }) {
  return (
    <Box sx={{ marginTop: "18px", marginBottom: "8px" }}>
      <Subtext
        fontSize={"11px"}
        fontFamily={"Poppins"}
        fontWeight={600}
        margin={0}
        color={themevals.colors.dkgrey}
      >
        {label.toUpperCase()}
      </Subtext>
    </Box>
  );
}
